import { useState } from "react";

const STEPS = [
  {
    icon: "ri-user-add-line",
    title: "Apply & Get Approved",
    desc: "Submit your application with your social handles and audience stats. Our team reviews within 24–48 hours and sends you a personalized affiliate dashboard link.",
    detail: "No minimum follower count required. We accept creators at all stages.",
  },
  {
    icon: "ri-store-2-line",
    title: "Browse & Pick Products",
    desc: "Access 50,000+ products from verified factories. Filter by category, commission rate, or trending status. Each product shows your exact earning per sale.",
    detail: "Products range from $10 to $500+. Higher-priced items = bigger commissions.",
  },
  {
    icon: "ri-share-line",
    title: "Share Your Unique Link",
    desc: "Get a personalized tracking link for every product. Post it on TikTok, YouTube, Twitter, Instagram — anywhere your audience is. Every click is tracked.",
    detail: "Links work across all platforms. Cookie tracking lasts 30 days.",
  },
  {
    icon: "ri-shopping-cart-line",
    title: "Buyer Purchases",
    desc: "When someone clicks your link and buys, the sale is automatically attributed to you. You earn commission on the full order value, including shipping.",
    detail: "Commission applies to the entire cart if they buy multiple items.",
  },
  {
    icon: "ri-bank-card-line",
    title: "Get Paid Weekly",
    desc: "Commissions are confirmed after the 7-day return window. Payments go out every Friday via PayPal, bank transfer, or crypto — your choice.",
    detail: "Minimum payout threshold is $25. No hidden fees or deductions.",
  },
];

const TIERS = [
  { tier: "Starter", followers: "1K – 10K", rate: "5%", color: "#8B6914", example: "$2.50", on: "$50 sale" },
  { tier: "Rising Star", followers: "10K – 100K", rate: "8%", color: "#B8860B", example: "$4.00", on: "$50 sale" },
  { tier: "Influencer", followers: "100K – 1M", rate: "12%", color: "#DAA520", example: "$6.00", on: "$50 sale" },
  { tier: "Mega Star", followers: "1M+", rate: "15%", color: "#FFD700", example: "$7.50", on: "$50 sale" },
];

const EXAMPLES = [
  {
    name: "Jessica M.",
    platform: "TikTok",
    icon: "ri-tiktok-line",
    followers: "2.3M",
    tier: "Mega Star",
    rate: "15%",
    product: "Wireless Earbuds Pro X200",
    price: "$89.99",
    sales: 142,
    commission: "$1,913.79",
    monthly: "$1,913.79",
    avatar: "https://public.readdy.ai/ai/img_res/91ad1de28f45f586305a6aa03eb2dc06.jpg",
  },
  {
    name: "Tyler B.",
    platform: "YouTube",
    icon: "ri-youtube-line",
    followers: "445K",
    tier: "Influencer",
    rate: "12%",
    product: "Resistance Bands Set 5-Level",
    price: "$34.99",
    sales: 87,
    commission: "$365.29",
    monthly: "$365.29",
    avatar: "https://readdy.ai/api/search-image?query=athletic%20man%20fitness%20trainer%20portrait%20confident%20smile&width=60&height=60&seq=calc2&orientation=squarish",
  },
  {
    name: "Emma R.",
    platform: "Instagram",
    icon: "ri-instagram-line",
    followers: "678K",
    tier: "Influencer",
    rate: "12%",
    product: "Vitamin C Brightening Serum",
    price: "$29.99",
    sales: 203,
    commission: "$730.56",
    monthly: "$730.56",
    avatar: "https://readdy.ai/api/search-image?query=latina%20woman%20lifestyle%20influencer%20portrait%20bright%20smile%20natural%20light&width=60&height=60&seq=calc3&orientation=squarish",
  },
];

export default function CommissionBreakdown() {
  const [activeStep, setActiveStep] = useState(0);
  const [calcPrice, setCalcPrice] = useState("89.99");
  const [calcSales, setCalcSales] = useState("50");
  const [calcTier, setCalcTier] = useState(2);

  const price = parseFloat(calcPrice) || 0;
  const sales = parseInt(calcSales) || 0;
  const rate = parseFloat(TIERS[calcTier].rate) / 100;
  const perSale = (price * rate).toFixed(2);
  const monthly = (price * rate * sales).toFixed(2);

  return (
    <section
      className="py-16 px-6 md:px-12 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #B8860B 0%, #DAA520 25%, #FFD700 50%, #DAA520 75%, #B8860B 100%)" }}
    >
      {/* Shimmer */}
      <div
        className="absolute inset-0 opacity-20"
        style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.15) 10px, rgba(255,255,255,0.15) 20px)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span
            className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4"
            style={{ background: "rgba(61,32,0,0.2)", color: "#3D2000" }}
          >
            How It Works
          </span>
          <h2 className="text-4xl font-extrabold mb-3" style={{ color: "#3D2000" }}>
            How Commissions Work
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: "#6B4500" }}>
            From your first post to your first payout — here's exactly how you earn money on StarBookPrime, step by step.
          </p>
        </div>

        {/* Step-by-step */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
          {/* Steps list */}
          <div className="space-y-3">
            {STEPS.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveStep(i)}
                className="w-full text-left rounded-2xl p-5 transition-all cursor-pointer border"
                style={
                  activeStep === i
                    ? { background: "rgba(61,32,0,0.18)", borderColor: "rgba(61,32,0,0.4)" }
                    : { background: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.4)" }
                }
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 text-lg"
                    style={
                      activeStep === i
                        ? { background: "#3D2000", color: "#FFD700" }
                        : { background: "rgba(61,32,0,0.15)", color: "#3D2000" }
                    }
                  >
                    <i className={s.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: activeStep === i ? "#FFD700" : "rgba(61,32,0,0.15)", color: "#3D2000" }}
                      >
                        Step {i + 1}
                      </span>
                      <span className="font-bold text-sm" style={{ color: "#3D2000" }}>{s.title}</span>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: "#6B4500" }}>{s.desc}</p>
                    {activeStep === i && (
                      <div
                        className="mt-3 text-xs px-3 py-2 rounded-lg flex items-start gap-2"
                        style={{ background: "rgba(255,255,255,0.4)", color: "#5C3D00" }}
                      >
                        <i className="ri-lightbulb-line flex-shrink-0 mt-0.5" />
                        <span>{s.detail}</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Right: Tiers + Calculator */}
          <div className="space-y-6">
            {/* Commission tiers */}
            <div className="rounded-2xl p-6 border" style={{ background: "rgba(255,255,255,0.3)", borderColor: "rgba(255,255,255,0.5)" }}>
              <h3 className="font-bold text-base mb-4" style={{ color: "#3D2000" }}>Commission Tiers</h3>
              <div className="space-y-3">
                {TIERS.map((t, i) => (
                  <div key={t.tier} className="flex items-center gap-3">
                    <div
                      className="w-2 h-8 rounded-full flex-shrink-0"
                      style={{ background: t.color }}
                    />
                    <div className="flex-1 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-bold" style={{ color: "#3D2000" }}>{t.tier}</div>
                        <div className="text-xs" style={{ color: "#6B4500" }}>{t.followers} followers</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-extrabold" style={{ color: "#3D2000" }}>{t.rate}</div>
                        <div className="text-xs" style={{ color: "#6B4500" }}>{t.example} {t.on}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Live calculator */}
            <div className="rounded-2xl p-6 border" style={{ background: "rgba(255,255,255,0.3)", borderColor: "rgba(255,255,255,0.5)" }}>
              <h3 className="font-bold text-base mb-4 flex items-center gap-2" style={{ color: "#3D2000" }}>
                <i className="ri-calculator-line" /> Earnings Calculator
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: "#5C3D00" }}>Product Price ($)</label>
                  <input
                    type="number"
                    value={calcPrice}
                    onChange={(e) => setCalcPrice(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl text-sm outline-none border"
                    style={{ background: "rgba(255,255,255,0.5)", borderColor: "rgba(61,32,0,0.2)", color: "#3D2000" }}
                    min="1"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: "#5C3D00" }}>Sales per Month</label>
                  <input
                    type="number"
                    value={calcSales}
                    onChange={(e) => setCalcSales(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl text-sm outline-none border"
                    style={{ background: "rgba(255,255,255,0.5)", borderColor: "rgba(61,32,0,0.2)", color: "#3D2000" }}
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: "#5C3D00" }}>Your Tier</label>
                  <div className="grid grid-cols-2 gap-2">
                    {TIERS.map((t, i) => (
                      <button
                        key={t.tier}
                        type="button"
                        onClick={() => setCalcTier(i)}
                        className="py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                        style={
                          calcTier === i
                            ? { background: "#3D2000", color: "#FFD700" }
                            : { background: "rgba(255,255,255,0.4)", color: "#3D2000", border: "1px solid rgba(61,32,0,0.15)" }
                        }
                      >
                        {t.tier} ({t.rate})
                      </button>
                    ))}
                  </div>
                </div>
                {/* Result */}
                <div className="rounded-xl p-4 text-center" style={{ background: "rgba(61,32,0,0.12)" }}>
                  <div className="text-xs mb-1" style={{ color: "#6B4500" }}>Per Sale</div>
                  <div className="text-2xl font-extrabold mb-2" style={{ color: "#3D2000" }}>${perSale}</div>
                  <div className="text-xs mb-1" style={{ color: "#6B4500" }}>Estimated Monthly Earnings</div>
                  <div className="text-3xl font-extrabold" style={{ color: "#3D2000" }}>${monthly}</div>
                  <div className="text-xs mt-2" style={{ color: "#8B6914" }}>
                    {sales} sales × ${perSale} = ${monthly}/mo
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Real payout examples */}
        <div className="mb-10">
          <h3 className="text-xl font-bold text-center mb-6" style={{ color: "#3D2000" }}>Real Payout Examples</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {EXAMPLES.map((ex) => (
              <div
                key={ex.name}
                className="rounded-2xl p-5 border"
                style={{ background: "rgba(255,255,255,0.3)", borderColor: "rgba(255,255,255,0.5)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img src={ex.avatar} alt={ex.name} className="w-12 h-12 rounded-full object-cover object-top" />
                  <div>
                    <div className="font-bold text-sm" style={{ color: "#3D2000" }}>{ex.name}</div>
                    <div className="text-xs flex items-center gap-1" style={{ color: "#6B4500" }}>
                      <i className={ex.icon} /> {ex.platform} · {ex.followers}
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-xs" style={{ color: "#5C3D00" }}>
                  <div className="flex justify-between">
                    <span>Product</span>
                    <span className="font-medium text-right max-w-[60%]">{ex.product}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Price</span>
                    <span className="font-medium">{ex.price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Commission Rate</span>
                    <span className="font-bold" style={{ color: "#3D2000" }}>{ex.rate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sales This Month</span>
                    <span className="font-medium">{ex.sales} orders</span>
                  </div>
                  <div
                    className="flex justify-between pt-2 mt-2 border-t font-bold text-sm"
                    style={{ borderColor: "rgba(61,32,0,0.15)", color: "#3D2000" }}
                  >
                    <span>Monthly Earned</span>
                    <span>{ex.commission}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href="#influencer-apply"
            className="inline-flex items-center gap-2 px-8 py-4 font-bold rounded-full transition-all hover:scale-105 cursor-pointer text-base whitespace-nowrap"
            style={{ background: "#3D2000", color: "#FFD700" }}
          >
            <i className="ri-money-dollar-circle-line" />
            Start Earning Today
          </a>
        </div>
      </div>
    </section>
  );
}
